import React from 'react';
import {
  CpuChipIcon,
  SparklesIcon,
  CloudIcon,
  CircleStackIcon,
  BeakerIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  DocumentTextIcon,
  LightBulbIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

export type RequestType = 'rag_query' | 'qbr_generation' | 'briefing_generation' | 'ui_navigation' | 'digest_generation' | 'progress_summary' | 'email_draft_generation' | 'account_radar' | 'risk_assessment' | 'account_narrative';

export type ServiceType = 'mcp' | 'llm' | 'pinecone' | 'firestore' | 'processing';

export interface PipelineStep {
  id: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  estimatedDuration: number; // seconds
  service: ServiceType;
}

export interface PipelineConfig {
  steps: PipelineStep[];
  totalEstimatedDuration: number;
}

const ServiceIcons: Record<ServiceType, React.ComponentType<{ className?: string }>> = {
  mcp: CpuChipIcon,
  llm: SparklesIcon,
  pinecone: CloudIcon,
  firestore: CircleStackIcon,
  processing: BeakerIcon,
};

// RAG Query Pipeline Steps
const ragQuerySteps: PipelineStep[] = [
  {
    id: 'mcp_classify',
    label: 'Analyzing query intent',
    description: 'MCP service is analyzing your query to determine the best approach',
    icon: ServiceIcons.mcp,
    estimatedDuration: 1.5,
    service: 'mcp',
  },
  {
    id: 'mcp_route',
    label: 'Routing to LLM service',
    description: 'Routing request to the appropriate LLM service',
    icon: ArrowRightIcon,
    estimatedDuration: 0.5,
    service: 'mcp',
  },
  {
    id: 'generate_embedding',
    label: 'Generating query embedding',
    description: 'Creating vector embedding for semantic search',
    icon: ServiceIcons.llm,
    estimatedDuration: 1.5,
    service: 'llm',
  },
  {
    id: 'pinecone_search',
    label: 'Searching vector database',
    description: 'Querying Pinecone for relevant context chunks',
    icon: ServiceIcons.pinecone,
    estimatedDuration: 1.5,
    service: 'pinecone',
  },
  {
    id: 'firestore_fetch',
    label: 'Fetching full context',
    description: 'Retrieving complete data from Firestore',
    icon: ServiceIcons.firestore,
    estimatedDuration: 1.5,
    service: 'firestore',
  },
  {
    id: 'filter_embeddings',
    label: 'Filtering embeddings',
    description: 'Processing and ranking search results',
    icon: ServiceIcons.processing,
    estimatedDuration: 0.75,
    service: 'processing',
  },
  {
    id: 'generate_response',
    label: 'Generating AI response',
    description: 'LLM is synthesizing insights from all sources',
    icon: ServiceIcons.llm,
    estimatedDuration: 3,
    service: 'llm',
  },
  {
    id: 'finalize',
    label: 'Finalizing results',
    description: 'Formatting response and preparing display',
    icon: CheckCircleIcon,
    estimatedDuration: 0.5,
    service: 'processing',
  },
];

// QBR Generation Pipeline Steps
const qbrGenerationSteps: PipelineStep[] = [
  {
    id: 'mcp_classify',
    label: 'Analyzing query intent',
    description: 'MCP service is analyzing your QBR request',
    icon: ServiceIcons.mcp,
    estimatedDuration: 1.5,
    service: 'mcp',
  },
  {
    id: 'prepare_qbr',
    label: 'Preparing QBR report',
    description: 'Validating customer and date range',
    icon: DocumentTextIcon,
    estimatedDuration: 0.5,
    service: 'processing',
  },
  {
    id: 'collect_data',
    label: 'Collecting customer data',
    description: 'Gathering customer profile and metadata',
    icon: ServiceIcons.firestore,
    estimatedDuration: 1,
    service: 'firestore',
  },
  {
    id: 'search_history',
    label: 'Searching conversation history',
    description: 'Querying Pinecone for relevant conversations',
    icon: ServiceIcons.pinecone,
    estimatedDuration: 2,
    service: 'pinecone',
  },
  {
    id: 'generate_quotes',
    label: 'Generating value quotes',
    description: 'Extracting customer testimonials and success stories',
    icon: ServiceIcons.llm,
    estimatedDuration: 4,
    service: 'llm',
  },
  {
    id: 'analyze_challenges',
    label: 'Analyzing challenges',
    description: 'Identifying support issues and concerns',
    icon: ServiceIcons.llm,
    estimatedDuration: 4,
    service: 'llm',
  },
  {
    id: 'identify_opportunities',
    label: 'Identifying opportunities',
    description: 'Discovering expansion and upsell potential',
    icon: ServiceIcons.llm,
    estimatedDuration: 4,
    service: 'llm',
  },
  {
    id: 'create_summary',
    label: 'Creating executive summary',
    description: 'Synthesizing high-level partnership overview',
    icon: ServiceIcons.llm,
    estimatedDuration: 5,
    service: 'llm',
  },
  {
    id: 'finalize_report',
    label: 'Finalizing report',
    description: 'Formatting QBR document and preparing sections',
    icon: CheckCircleIcon,
    estimatedDuration: 1,
    service: 'processing',
  },
];

// Briefing Generation Pipeline Steps
const briefingGenerationSteps: PipelineStep[] = [
  {
    id: 'mcp_classify',
    label: 'Analyzing query intent',
    description: 'MCP service is analyzing your briefing request',
    icon: ServiceIcons.mcp,
    estimatedDuration: 1.5,
    service: 'mcp',
  },
  {
    id: 'gather_communications',
    label: 'Gathering communications',
    description: 'Collecting messages from Slack, Zendesk, and Jira',
    icon: ServiceIcons.firestore,
    estimatedDuration: 2,
    service: 'firestore',
  },
  {
    id: 'analyze_sentiment',
    label: 'Analyzing sentiment',
    description: 'Evaluating tone and sentiment of communications',
    icon: ServiceIcons.llm,
    estimatedDuration: 2,
    service: 'llm',
  },
  {
    id: 'extract_key_points',
    label: 'Extracting key points',
    description: 'Identifying important topics and discussion points',
    icon: ServiceIcons.llm,
    estimatedDuration: 2.5,
    service: 'llm',
  },
  {
    id: 'generate_briefing',
    label: 'Generating briefing',
    description: 'Creating structured briefing document',
    icon: ServiceIcons.llm,
    estimatedDuration: 3,
    service: 'llm',
  },
  {
    id: 'finalize_document',
    label: 'Finalizing document',
    description: 'Formatting and preparing briefing for display',
    icon: CheckCircleIcon,
    estimatedDuration: 0.5,
    service: 'processing',
  },
];

// UI Navigation Pipeline Steps (simplified)
const uiNavigationSteps: PipelineStep[] = [
  {
    id: 'mcp_classify',
    label: 'Analyzing navigation request',
    description: 'MCP service is determining navigation destination',
    icon: ServiceIcons.mcp,
    estimatedDuration: 0.5,
    service: 'mcp',
  },
  {
    id: 'navigate',
    label: 'Navigating',
    description: 'Routing to requested page',
    icon: ArrowRightIcon,
    estimatedDuration: 0.2,
    service: 'processing',
  },
];

// Weekly Digest Generation Pipeline Steps
const digestGenerationSteps: PipelineStep[] = [
  {
    id: 'mcp_classify',
    label: 'Analyzing digest request',
    description: 'MCP service is processing your weekly digest request',
    icon: ServiceIcons.mcp,
    estimatedDuration: 1,
    service: 'mcp',
  },
  {
    id: 'collect_customers',
    label: 'Analyzing portfolio',
    description: 'Gathering all customer health scores and status',
    icon: ServiceIcons.firestore,
    estimatedDuration: 2,
    service: 'firestore',
  },
  {
    id: 'categorize_customers',
    label: 'Categorizing accounts',
    description: 'Identifying at-risk and thriving accounts',
    icon: ServiceIcons.processing,
    estimatedDuration: 1.5,
    service: 'processing',
  },
  {
    id: 'gather_context',
    label: 'Gathering context',
    description: 'Retrieving recent conversations and issues',
    icon: ServiceIcons.pinecone,
    estimatedDuration: 3,
    service: 'pinecone',
  },
  {
    id: 'analyze_risks',
    label: 'Analyzing risks',
    description: 'Evaluating risk factors and mitigation strategies',
    icon: ServiceIcons.llm,
    estimatedDuration: 4,
    service: 'llm',
  },
  {
    id: 'identify_gaps',
    label: 'Identifying product gaps',
    description: 'Finding common feature requests and issues',
    icon: ServiceIcons.llm,
    estimatedDuration: 3,
    service: 'llm',
  },
  {
    id: 'generate_digest',
    label: 'Generating digest',
    description: 'Creating executive summary and recommendations',
    icon: LightBulbIcon,
    estimatedDuration: 4,
    service: 'llm',
  },
  {
    id: 'finalize',
    label: 'Finalizing digest',
    description: 'Formatting and preparing display',
    icon: CheckCircleIcon,
    estimatedDuration: 0.5,
    service: 'processing',
  },
];

// Email Draft Generation Pipeline Steps
const emailDraftGenerationSteps: PipelineStep[] = [
  {
    id: 'mcp_classify',
    label: 'Analyzing email request',
    description: 'MCP service is processing your email draft request',
    icon: ServiceIcons.mcp,
    estimatedDuration: 1,
    service: 'mcp',
  },
  {
    id: 'gather_context',
    label: 'Gathering customer context',
    description: 'Retrieving recent interactions and conversations',
    icon: ServiceIcons.pinecone,
    estimatedDuration: 2,
    service: 'pinecone',
  },
  {
    id: 'analyze_relationship',
    label: 'Analyzing relationship',
    description: 'Understanding customer sentiment and engagement',
    icon: ServiceIcons.llm,
    estimatedDuration: 2,
    service: 'llm',
  },
  {
    id: 'draft_email',
    label: 'Drafting email',
    description: 'Generating professional email content',
    icon: ServiceIcons.llm,
    estimatedDuration: 3,
    service: 'llm',
  },
  {
    id: 'finalize',
    label: 'Finalizing draft',
    description: 'Formatting and preparing email for review',
    icon: CheckCircleIcon,
    estimatedDuration: 0.5,
    service: 'processing',
  },
];

// Progress Summary Generation Pipeline Steps
const progressSummarySteps: PipelineStep[] = [
  {
    id: 'mcp_classify',
    label: 'Analyzing progress request',
    description: 'MCP service is processing your progress tracking request',
    icon: ServiceIcons.mcp,
    estimatedDuration: 1,
    service: 'mcp',
  },
  {
    id: 'search_discussions',
    label: 'Searching team discussions',
    description: 'Finding relevant internal Slack conversations',
    icon: ServiceIcons.pinecone,
    estimatedDuration: 2,
    service: 'pinecone',
  },
  {
    id: 'extract_updates',
    label: 'Extracting updates',
    description: 'Identifying key progress updates and decisions',
    icon: ServiceIcons.llm,
    estimatedDuration: 3,
    service: 'llm',
  },
  {
    id: 'identify_blockers',
    label: 'Identifying blockers',
    description: 'Finding challenges and obstacles mentioned',
    icon: ServiceIcons.llm,
    estimatedDuration: 2,
    service: 'llm',
  },
  {
    id: 'generate_summary',
    label: 'Generating summary',
    description: 'Creating one-page executive summary',
    icon: ChartBarIcon,
    estimatedDuration: 3,
    service: 'llm',
  },
  {
    id: 'finalize',
    label: 'Finalizing summary',
    description: 'Formatting and preparing display',
    icon: CheckCircleIcon,
    estimatedDuration: 0.5,
    service: 'processing',
  },
];

export function getPipelineConfig(requestType: RequestType): PipelineConfig {
  let steps: PipelineStep[];
  
  switch (requestType) {
    case 'rag_query':
      steps = ragQuerySteps;
      break;
    case 'qbr_generation':
      steps = qbrGenerationSteps;
      break;
    case 'briefing_generation':
      steps = briefingGenerationSteps;
      break;
    case 'ui_navigation':
      steps = uiNavigationSteps;
      break;
    case 'digest_generation':
      steps = digestGenerationSteps;
      break;
    case 'progress_summary':
      steps = progressSummarySteps;
      break;
    case 'email_draft_generation':
      steps = emailDraftGenerationSteps;
      break;
    case 'account_radar':
      steps = digestGenerationSteps;
      break;
    case 'risk_assessment':
    case 'account_narrative':
      steps = ragQuerySteps;
      break;
    default:
      steps = ragQuerySteps;
  }
  
  const totalEstimatedDuration = steps.reduce((sum, step) => sum + step.estimatedDuration, 0);
  
  return {
    steps,
    totalEstimatedDuration,
  };
}

export function calculateProgress(currentStepIndex: number, steps: PipelineStep[]): number {
  if (currentStepIndex < 0) return 0;
  if (currentStepIndex >= steps.length) return 100;
  
  const completedDuration = steps
    .slice(0, currentStepIndex)
    .reduce((sum, step) => sum + step.estimatedDuration, 0);
  
  const currentStepProgress = 0.5; // Assume 50% through current step
  const currentStepDuration = steps[currentStepIndex]?.estimatedDuration || 0;
  
  const totalDuration = steps.reduce((sum, step) => sum + step.estimatedDuration, 0);
  const progress = ((completedDuration + currentStepProgress * currentStepDuration) / totalDuration) * 100;
  
  return Math.min(100, Math.max(0, progress));
}

